/**
 * useAutoSelectUser Hook
 * 
 * Automatically selects the current user in document context when:
 * - User is logged in and ready
 * - User does not have manager permission
 * 
 * This hook fixes React Hook dependency warnings by encapsulating
 * the auto-selection logic with all proper dependencies.
 * 
 * @file src/hooks/useAutoSelectUser.ts
 * @created October 11, 2025
 */

import { useEffect } from 'react';
import { useDocumentContext } from '../contexts/DocumentContext';
import { UserDetails } from '../types/User';

interface UseAutoSelectUserProps {
  /** Whether user data is ready */
  userReady: boolean;
  /** Current user details */
  userDetails: UserDetails | null;
  /** Function to check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
}

/**
 * Hook to automatically select the current user in document context
 * when they don't have manager permissions
 */
export const useAutoSelectUser = ({
  userReady,
  userDetails,
  hasPermission,
}: UseAutoSelectUserProps): void => {
  const { setSelectedUser } = useDocumentContext();

  useEffect(() => {
    // Only auto-select if:
    // 1. User data is ready
    // 2. User exists and has an agency
    // 3. User does NOT have manager permission
    if (
      userReady &&
      userDetails &&
      userDetails.agency &&
      !hasPermission('user_manage_agency')
    ) {
      // Transform enhanced user details to UserDetails format
      // for compatibility with existing document context
      setSelectedUser({
        id: userDetails.id,
        fullNames: userDetails.fullNames ?? null,
        lastName: userDetails.lastName ?? null,
        fullName: userDetails.fullName,
        displayName: userDetails.displayName ?? '',
        email: userDetails.email ?? '',
        identificationType: (userDetails.identificationType ?? null) as 'RSA ID' | 'Passport' | 'Foreign ID' | null,
        idNumber: userDetails.idNumber ?? null,
        dateOfBirth: userDetails.dateOfBirth ?? null,
        gender: (userDetails.gender ?? null) as 'Male' | 'Female' | 'Other' | null,
        mobileNumber: userDetails.mobileNumber ?? null,
        // Physical address fields
        unitNumber: userDetails.unitNumber ?? null,
        complex: userDetails.complex ?? null,
        streetNumber: userDetails.streetNumber ?? null,
        streetName: userDetails.streetName ?? null,
        suburb: userDetails.suburb ?? null,
        city: userDetails.city ?? null,
        postalCode: userDetails.postalCode ?? null,
        fullPhysicalAddress: userDetails.fullPhysicalAddress ?? null,
        // Postal address fields
        postalAddressSameAsResidential: userDetails.postalAddressSameAsResidential ?? true,
        postalUnitNumber: userDetails.postalUnitNumber ?? null,
        postalComplex: userDetails.postalComplex ?? null,
        postalStreetNumber: userDetails.postalStreetNumber ?? null,
        postalStreetName: userDetails.postalStreetName ?? null,
        postalSuburb: userDetails.postalSuburb ?? null,
        postalCity: userDetails.postalCity ?? null,
        postalPostalCode: userDetails.postalPostalCode ?? userDetails.postalPostalCode ?? null,
        accountStatus: (userDetails.accountStatus ?? null) as 'Pending' | 'Active' | 'Suspended' | 'Archived' | null,
        profileComplete: userDetails.profileComplete ?? false,
        emailVerified: userDetails.emailVerified ?? false,
        agency: userDetails.agency as any,
        role: userDetails.role,
        roleName: userDetails.role,
        permissions: userDetails.permissions ?? [],
        accessLevel: userDetails.accessLevel ?? 1,
        archived: userDetails.archived ?? false,
      });
    }
  }, [userReady, userDetails, hasPermission, setSelectedUser]);
};
